import { useEffect, useRef } from 'react';
import { Network, Shield } from 'lucide-react';

const GAIL_IMG = "https://customer-assets-jai6qajn.emergentagent.net/job_fcc7b125-4234-459f-a433-a6bfcd5aa6ad/artifacts/iicfilw0_GWS_B%26W_Headshot1.png";
const CHRISTOPHE_IMG = "https://customer-assets-jai6qajn.emergentagent.net/job_fcc7b125-4234-459f-a433-a6bfcd5aa6ad/artifacts/iarph7tw_CHS_B%26W_Headshot2.png";

export default function OurStory() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1 }
    );
    const els = sectionRef.current?.querySelectorAll('.reveal');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="our-story"
      ref={sectionRef}
      data-testid="our-story-section"
      className="relative py-20 md:py-28 bg-[#efeee9]"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-5xl">
        {/* Header */}
        <div className="text-center mb-14 reveal">
          <p
            className="text-sm uppercase tracking-[0.3em] text-[#1a7a7a] mb-3 font-medium"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Leadership
          </p>
          <h2
            className="text-4xl md:text-5xl font-semibold tracking-tight text-[#1a1a1a]"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Our Founders
          </h2>
        </div>

        {/* Founders */}
        <div className="mb-16 reveal">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Gail */}
            <div className="flex flex-col items-center text-center p-8 rounded-2xl bg-white border border-gray-200 hover:border-[#1a7a7a]/30 hover:shadow-lg transition-all duration-300" data-testid="founder-gail">
              <img
                src={GAIL_IMG}
                alt="Gail Warrior"
                className="w-40 h-40 rounded-full object-cover object-top border-3 border-[#1a7a7a]/20 mb-5 bg-[#1a1a1a]"
              />
              <h4
                className="text-xl font-semibold text-[#1a1a1a] mb-1"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Gail Warrior
              </h4>
              <p className="text-[#1a7a7a] text-xs uppercase tracking-wider font-semibold mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Co-Founder
              </p>
              <p className="text-gray-500 text-sm leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Former C-suite executive with two successful exits and decades of cross border business leadership. Gail brings operational rigor and board-level strategic thinking to every engagement, translating complex jurisdictional decisions into executable architecture.
              </p>
            </div>

            {/* Christophe */}
            <div className="flex flex-col items-center text-center p-8 rounded-2xl bg-white border border-gray-200 hover:border-[#1a7a7a]/30 hover:shadow-lg transition-all duration-300" data-testid="founder-christophe">
              <img
                src={CHRISTOPHE_IMG}
                alt="Dr. Christophe Suchy"
                className="w-40 h-40 rounded-full object-cover object-top border-3 border-[#1a7a7a]/20 mb-5 bg-[#1a1a1a]"
              />
              <h4
                className="text-xl font-semibold text-[#1a1a1a] mb-1"
                style={{ fontFamily: "'Playfair Display', serif" }}
              >
                Dr. Christophe Suchy
              </h4>
              <p className="text-[#1a7a7a] text-xs uppercase tracking-wider font-semibold mb-4" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Co-Founder
              </p>
              <p className="text-gray-500 text-sm leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                Raised across multiple continents, fluent in six languages, holder of multiple citizenships and residencies across four countries. With a PhD from the Sorbonne and decades of entrepreneurial experience across Europe, the Middle East, Latin America, and Asia, Christophe brings rare depth to jurisdictional strategy and cross-border structuring.
              </p>
            </div>
          </div>
        </div>

        {/* Our Network */}
        <div className="mb-16 reveal">
          <div className="rounded-2xl bg-white border border-gray-200 p-8 md:p-10">
            <div className="flex items-start gap-5">
              <div className="w-12 h-12 rounded-xl bg-[#1a7a7a]/10 border border-[#1a7a7a]/15 flex items-center justify-center shrink-0 mt-1">
                <Network className="text-[#1a7a7a]" size={24} />
              </div>
              <div>
                <h3
                  className="text-2xl font-semibold text-[#1a1a1a] mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Our Network
                </h3>
                <div className="space-y-4 text-gray-500 text-sm leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  <p>
                    Behind every GCC engagement is a vetted ecosystem of specialists: immigration counsel, international tax advisors, licensed banking professionals, real estate advisors, and government liaison partners, selected jurisdiction by jurisdiction based on track record and alignment with our standards.
                  </p>
                  <p className="text-[#1a1a1a] font-medium">
                    This is how a retained advisory delivers institutional-grade outcomes: not by hiring everyone in-house, but by knowing exactly who to call, anywhere in the world.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Why Retained */}
        <div className="reveal">
          <div className="rounded-2xl bg-white border border-gray-200 p-8 md:p-10">
            <div className="flex items-start gap-5">
              <div className="w-12 h-12 rounded-xl bg-[#1a7a7a]/10 border border-[#1a7a7a]/15 flex items-center justify-center shrink-0 mt-1">
                <Shield className="text-[#1a7a7a]" size={24} />
              </div>
              <div>
                <h3
                  className="text-2xl font-semibold text-[#1a1a1a] mb-4"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  Why Retained Advisory
                </h3>
                <div className="space-y-4 text-gray-500 text-sm leading-relaxed" style={{ fontFamily: "'Montserrat', sans-serif" }}>
                  <p>
                    The clients we serve do not want a call center. They want counsel that is personally invested in the outcome. At GCC, every engagement is handled directly by our founders, not delegated to junior staff.
                  </p>
                  <p className="text-[#1a1a1a] font-semibold text-base" style={{ fontFamily: "'Playfair Display', serif" }}>
                    Small by design. Global by reach. Accountable at the top.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
